import type { RootState } from '@src/store';
import { selectBizSkill as selectBizSkillsData } from '@src/store/slices/biz_skills';
import { selectPrivateActivity as selectPrivateActivitiesData } from '@src/store/slices/private_activity';
import { selectSkill as selectSkillsData } from '@src/store/slices/skill';
import { createSelector } from 'reselect';

/**
 * Redux状態用のメモ化セレクター
 *
 * reselectライブラリを使用してRedux状態から効率的にデータを選択するセレクター群です。
 * 不要な再レンダリングを防ぐため、関連する状態が変更された場合のみ新しい値を返します。
 *
 * @fileoverview 経歴書データのメモ化セレクター定義
 * @since 1.0.0
 * @author Resume Commit Team
 */

/**
 * 基本セレクター群
 *
 * RootStateから個別の状態スライスを取得する基本セレクターです。
 * これらのセレクターは createSelector の入力セレクターとして使用されます。
 */

/**
 * 連絡先情報セレクター (分離されたスライス)
 *
 * @param {RootState} state - Redux ルート状態
 * @returns {ContactState} 連絡先情報
 */
export const selectContact = (state: RootState) => state.resumeContact;

/** 職歴情報セレクター (分離されたスライス) */
export const selectHistories = (state: RootState) => state.resumeHistory.histories;

/** 学歴情報セレクター */
export const selectEducationalBackground = (state: RootState) => state.educationalBackground;

/** 技術スキル情報セレクター */
export const selectSkills = selectSkillsData;

/** プロジェクト情報セレクター */
export const selectProjects = (state: RootState) => state.project.projects;

/** ビジネススキル情報セレクター */
export const selectBizSkills = selectBizSkillsData;

/** キャリア要約セレクター */
export const selectCareerSummary = (state: RootState) => state.careerSummary.content;

/** 志望動機セレクター */
export const selectNextCareer = (state: RootState) => state.nextCareer.content;

/** その他活動情報セレクター */
export const selectPrivateActivities = selectPrivateActivitiesData;

/** スキルコメントセレクター */
export const selectSkillComment = (state: RootState) => state.skillComment.content;

/** スキルタイプセレクター */
export const selectSkillType = (state: RootState) => state.skillType.type;

/** 個人情報マスキング設定セレクター (統合UIスライス) */
export const selectBlind = (state: RootState) => state.ui.blind;

/**
 * メモ化された経歴書データセレクター
 *
 * Redux状態から経歴書作成に必要なすべてのデータを効率的に取得するメモ化セレクターです。
 * reselectライブラリの createSelector を使用し、入力セレクターの値が変更された場合のみ
 * 新しいオブジェクトを返します。これにより不要な再レンダリングを防ぎ、
 * アプリケーションのパフォーマンスを最適化します。
 *
 * @example
 * ```tsx
 * import { useSelector } from 'react-redux';
 * import { selectResumeData } from '@src/selectors/resumeDataSelector';
 *
 * function MyComponent() {
 *   const resumeData = useSelector(selectResumeData);
 *
 *   return (
 *     <div>
 *       <h1>{resumeData.contact.name}</h1>
 *       <p>職歴数: {resumeData.history.length}</p>
 *       <p>スキル数: {resumeData.skills.length}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns {Object} 統合された経歴書データ
 * @returns {ContactState} returns.contact - 連絡先情報
 * @returns {History[]} returns.history - 職歴情報配列
 * @returns {EducationalBackground} returns.educationalBackground - 学歴情報
 * @returns {Skill[]} returns.skills - 技術スキル配列
 * @returns {Project[]} returns.project - プロジェクト情報配列
 * @returns {string[]} returns.bizSkills - ビジネススキル配列
 * @returns {string} returns.careerSummary - キャリア要約
 * @returns {string} returns.nextCareer - 志望動機
 * @returns {OtherActivity[]} returns.otherActivity - その他活動配列
 * @returns {string} returns.skillsComment - スキルコメント
 * @returns {SkillType} returns.skillType - スキルタイプ
 * @returns {boolean} returns.isBlind - 個人情報マスキング設定
 *
 * @since 1.0.0
 * @author Resume Commit Team
 */
export const selectResumeData = createSelector(
  [
    selectContact,
    selectHistories,
    selectEducationalBackground,
    selectSkills,
    selectProjects,
    selectBizSkills,
    selectCareerSummary,
    selectNextCareer,
    selectPrivateActivities,
    selectSkillComment,
    selectSkillType,
    selectBlind,
  ],
  (
    contact,
    history,
    educationalBackground,
    skill,
    project,
    bizSkill,
    careerSummary,
    nextCareer,
    privateActivity,
    skillComment,
    skillType,
    blind
  ) => ({
    contact,
    history,
    educationalBackground,
    skills: skill,
    project,
    bizSkills: bizSkill,
    careerSummary,
    nextCareer,
    otherActivity: privateActivity,
    skillsComment: skillComment,
    skillType,
    isBlind: blind,
  })
);
